import { action, computed, IObservableArray, observable } from "mobx";
import { IDocumentsView } from "../Document/DocumentsView";
import * as DocumentReader from "../DocumentReader";
import { FilterMode } from "../Shared";
import { AreaUiInfoImpl } from "../States/AreaUiInfo";
import { AdditionalFilterValuesWizardStore } from "../Stores/AdditionalFilterValuesWizardStore";
import { AreaStore } from "../Stores/AppStore";
import { DialogStore } from "../Stores/DialogStore";
import { DocumentFilter, IDocumentFilter } from "../Stores/DocumentFilterStore";
import { Strings } from "../Stores/StringsStore";
import { TypeAheadStore } from "./TypeAheadStore";
import { UiRepos } from "./UiStore";


export class DocumentSearchStore extends AreaStore implements IDocumentsView {

	constructor(documentFilter: IDocumentFilter, updateSearchResult?: (ids: string[]) => void) {
		super();
		this.documentFilter = documentFilter;
		this.updateSearchResult = updateSearchResult;
	}

	documentFilter: IDocumentFilter;

	/* show only Name column */
	showOnlyName = false;

	/**
	* Reader AreaUiInfo implementation.
	*/
	areaUiInfo: AreaUiInfoImpl = new AreaUiInfoImpl(UiRepos.READER_UI_INFO);

	updateSearchResult?: (ids: string[]) => void;

	/**
	* Used for storing recently viewed documents temporarly
	*/
	@observable tempRecentlyViewedDocs: string[] = [];
	/*pagination params*/
	top: number = 50;
	skip: number = 0;
	sortBy: DocumentReader.SortBy[] = [];
	scrollPercentage: number = 40;

	noResultMsg: string = Strings.reader.readerPlaceholderTextforEmptyResult;
	/**
	 * The api is busy
	 */
	@observable isBusy: boolean = false;
	/**
	 * Search term for document query
	 */
	@observable searchTerm: string = "";

	@observable lastDeselectedFilterValue: DocumentReader.SearchFilterValue = null;

	/**
	* Wizard store for selection of additional filter values.
	*/
	additionalFilterValuesWizardState: AdditionalFilterValuesWizardStore = new AdditionalFilterValuesWizardStore(this);

	/**
	* TypeAhead store 
	*/
	typeAheadStore: TypeAheadStore = new TypeAheadStore(this);

	/**
	* All document filter view models.
	*/
	documentSearchFilters: IObservableArray<DocumentReader.DocumentSearchFilter> = observable.array<DocumentReader.DocumentSearchFilter>();

	@observable fileCount: number = -1;

	/**
	 * The final search result.
	 */
	@observable searchResult: DocumentReader.DocumentResult = new DocumentReader.DocumentResult();

	/**
	 * Triggers the search when a document filter has changed or the search button was clicked.
	 * @param resetPagination used to determine whether need to reset pagination params or not
	 */
	public triggerSearch(resetPagination?: boolean, isScrolling: boolean = false) {
		if (!this.isBusy) {
			this.isBusy = true;
			if (!isScrolling) { this.typeAheadStore.resetSuggestions(); }
			if (resetPagination) { this.resetPaginationParams(); }
			let query = this.currentDocumentQuery;
			if (!isScrolling) { this.getSearchFilters(); }
			DocumentReader.queryDocumentsAsync(query).then(result => {
				if (!isScrolling) { this.searchResult.headers.replace(result.headers); }
				if (this.skip == 0) {
					this.searchResult.documents.replace(result.documents);
				}
				else {
					this.searchResult.documents.push(...result.documents);
				}
				if (this.updateSearchResult) {
					this.updateSearchResult(this.searchResult.documents.map(d => d.documentId));
				}
				this.isBusy = false;
			}).catch(error => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
				this.isBusy = false;
			});
		}
	}

	public favouriteChange(documentId: string) {
		//do nothing now
	}

	/**
	 * Get all document search filters.
	 * @param onResolve The callback which is triggerd after the api call is resolved.
	 * @param query The document query incl. all current filters. 
	 */
	@action getSearchFilters = () => {
		DocumentReader.getDocumentFiltersAsync(this.currentDocumentQuery).then((filters: DocumentReader.DocumentSearchFilters) => {
			this.clearDeletedFilters(filters);
			this.documentSearchFilters.replace(filters.searchFilters);
			this.fileCount = filters.fileCount;
			if (this.documentFilter === DocumentFilter) {
				this.areaUiInfo.searchTerm = this.searchTerm;
			}
		}).catch((error) => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
			this.isBusy = false;
		});
	}

	@action clearDeletedFilters(filters: DocumentReader.DocumentSearchFilters) {
		let filterNamesToDelete: string[] = filters.searchFilters.map(function (filter) { return filter.name; });
		this.documentFilter.getDocumentFilters().forEach(filter => {
			if (filterNamesToDelete.indexOf(filter.name) === -1) {
				this.documentFilter.deleteFilterByKey(filter.name);
			}
		});
	}

	@computed get scrollLock(): boolean {
		return (this.searchResult.documents.length >= this.skip + this.top)
	}

	@action loadUiSettings() {
		this.areaUiInfo.asideWidth = UiRepos.readerUiInfo.asideWidth;
		this.areaUiInfo.isCollapsed = UiRepos.readerUiInfo.isCollapsed;
		this.areaUiInfo.searchTerm = UiRepos.readerUiInfo.searchTerm;
		if (this.documentFilter === DocumentFilter) {
			this.searchTerm = UiRepos.readerUiInfo.searchTerm ? UiRepos.readerUiInfo.searchTerm : "";
		}
	}

	/**
	*	Reset the params used fr pagination
	*/
	resetPaginationParams = () => {
		this.top = 50;
		this.skip = 0;
		this.scrollPercentage = 40;
		this.searchResult.documents.clear();
		this.searchResult.headers.clear();
	}

	/**
	*	Reset the filters
	* @param resetFilterException clear all filter values except this
	*/
	@action resetFilter = (resetFilterException: string = "") => {
		if (resetFilterException) {
			let filter: DocumentReader.DocumentFilter = null;
			if (this.documentFilter.hasFilter(resetFilterException)) {
				filter = this.documentFilter.findFilter(resetFilterException);
				this.documentFilter.clearFilter();
				this.documentFilter.setFilter(filter);
			}
			else {
				this.documentFilter.clearFilter();
			}
		}
		else {
			this.documentFilter.clearFilter();
		}
	}

	/**
	 * Creates the current DocumentQuery object for triggering the search.
	 */
	get currentDocumentQuery(): DocumentReader.DocumentQuery {
		let query: DocumentReader.DocumentQuery = new DocumentReader.DocumentQuery();
		query.fileVariants = null;
		query.searchExpression = this.searchTerm;
		query.filters = this.documentFilter.getDocumentFilters();
		query.skip = this.skip;
		query.top = this.top;
		query.sortBy = this.sortBy;
		return query;
	}

	/**
	 * Reset the 'isSelected' property of all filter values except the selectedFilterValue to false.
	 * @param selectedFilterValue
	 * @param filterValues
	 */
	resetFilterValuesOfTypeSingle(selectedFilterValue: DocumentReader.SearchFilterValue, filterValues: DocumentReader.SearchFilterValue[]) {
		filterValues.forEach((singleFilterValue) => {
			if (selectedFilterValue != singleFilterValue) {
				singleFilterValue.isSelected = false;
			}
		});
	}

	/**
	 * Mapping the selected filter values to the documentFilter object which is used to build the query object.
	 * @param searchFilter The select filter.
	 * @param value The selected filter value.
	 */
	@action setDocumentFilter = (searchFilter: DocumentReader.DocumentSearchFilter, value: DocumentReader.SearchFilterValue) => {
		let filter: DocumentReader.DocumentFilter = null,
			name: string = searchFilter.name;
		if (this.documentFilter.hasFilter(name)) {
			filter = this.documentFilter.findFilter(name);
			if (searchFilter.filterMode == FilterMode.Single) {
				filter.values.clear();
			}
			let values = filter.values as IObservableArray<string>;
			if (value.isSelected) {
				filter.values.push(value.value);
			}
			else {
				values.remove(value.value);
			}
		}
		else {
			filter = new DocumentReader.DocumentFilter();
			filter.name = name;
			if (value.isSelected) {
				filter.values.push(value.value);
			}
		}
		if (filter.values.length > 0) {
			this.documentFilter.setFilter(filter);
		}
		else {
			this.documentFilter.deleteFilter(filter);
		}
	}
}
